import { BmTask } from "@/app/services/generated/bmtaskService";
import type { EnumBmEntityType } from "@/app/services/generated/enums/enumBmEntityType.generated";
import type Guid from "@/app/types/common/guid";
import { showModalDialog } from "@/components/platform/common/showModalDialog";
import AddEditTaskDialog from "@/components/pages/tasks/subcomponents/AddEditTaskDialog.vue";
import type { EnumTaskRepeatBasis } from "@/app/services/generated/enums/enumTaskRepeatBasis.generated";
import type { Maybe } from "@/components/utils/typeHelpers";
import { TemporalAdjusters } from "@js-joda/core";
import type { DayOfWeek, LocalDate } from "@js-joda/core";
import { EnumDayOfWeek } from "@/app/services/generated/enums/enumDayOfWeek.generated";
import { appendOrdinalSuffix, translate } from "@/plugins/i18n";
import Key from "@/app/types/common/key";
import { isNotNullOrUndefined, isNullish } from "@/app/apiutils";
import type { PlannedSalesVisit } from "@/app/services/generated/salesvisitService";

export async function showAddTaskModalDialog(
  entityType: EnumBmEntityType,
  entityId: Guid
) {
  const task = new BmTask();
  task.associatedBmEntityType = entityType;
  task.associatedBmEntityId = entityId;
  const result = await showModalDialog(AddEditTaskDialog, {
    props: {
      addOrEditTask: task,
    },
  });

  return result.wasSuccessful ? (result.value ?? null) : null;
}

export function repeatBasisParameterMax(basis: Maybe<EnumTaskRepeatBasis>) {
  return basis?.isNthMatchingDayOfEachMonth() ? 4 : undefined;
}

export function repeatBasisNeedsParameter(basis: Maybe<EnumTaskRepeatBasis>) {
  return (
    (basis?.isNthMatchingDayOfEachMonth() ||
      basis?.isEveryNWeeks() ||
      basis?.isEveryNMonths()) ??
    false
  );
}

export function getRepeatBasisText(opts: {
  repeatBasis: EnumTaskRepeatBasis;
  repeatParameter?: number;
  date: Maybe<LocalDate>;
}) {
  const dayOfWeek = opts.date
    ? EnumDayOfWeek.getByKey(opts.date.dayOfWeek().value())
    : EnumDayOfWeek.none;
  const dayOfWeekTranslated = dayOfWeek.translation;

  // If repeat parameter known use it for the ordinal (eg "2 weeks", "2nd Thursday of Month")
  // Else just use "x" (eg "x weeks", "x Thursday of Month")
  return translate(Key.fromString(opts.repeatBasis.translationKey), {
    day: dayOfWeekTranslated,
    repeatParameter: opts.repeatParameter
      ? opts.repeatParameter.toString()
      : "x",
    ordinalNumber: opts.repeatParameter
      ? appendOrdinalSuffix(opts.repeatParameter)
      : "x",
  });
}

function nthOrLastOfMonth(date: LocalDate, dayOfWeek: DayOfWeek, n: number) {
  // JsJoda does not naturally map non-existant "5th" eg Fridays to the last
  // So we have to do that ourselves. Instead base on the First friday, and add the relevant number of weeks.
  const first = date.with(TemporalAdjusters.firstInMonth(dayOfWeek));
  const last = date.with(TemporalAdjusters.lastInMonth(dayOfWeek));
  const nth = first.plusWeeks(n - 1);
  return nth > last ? last : nth;
}

export function getRepeatDate(opts: {
  repeatBasis: EnumTaskRepeatBasis;
  repeatParameter?: number;
  date: Maybe<LocalDate>;
}) {
  const date = opts.date;
  const basis = opts.repeatBasis;
  const param = opts.repeatParameter ?? 1;

  if (date) {
    const dayOfWeek = date.dayOfWeek();
    if (basis.isEveryDay()) {
      return date.plusDays(1);
    } else if (basis.isEveryNMonths()) {
      // Correctly adjusts to last day of month if naturally beyond end of month.
      return date.plusMonths(param);
    } else if (basis.isEveryNWeeks()) {
      return date.plusWeeks(param);
    } else if (basis.isEveryWeekday()) {
      // dayOfWeek is 1 (monday) to 7 (sunday).
      // Advance Mon(1), Tue(1), Wed(1), Thur(1), Fri(3), Sat(2), Sun(1)
      return date.plusDays(dayOfWeek.value() < 5 ? 1 : 8 - dayOfWeek.value());
    } else if (basis.isLastMatchingDayOfEachMonth()) {
      // date.with(TemporalAdjusters.lastInMonth(DayOfWeek.THURSDAY)) returns the last thursday in the month of date
      const lastInSourceMonth = date.with(
        TemporalAdjusters.lastInMonth(dayOfWeek)
      );
      // Return either the last of the current month, or the last of the next month
      return lastInSourceMonth > date
        ? lastInSourceMonth
        : date.plusMonths(1).with(TemporalAdjusters.lastInMonth(dayOfWeek));
    } else if (basis.isNthMatchingDayOfEachMonth()) {
      // Return either the match of the current month, or of the next month
      const nthInSourceMonth = nthOrLastOfMonth(date, dayOfWeek, param);
      return nthInSourceMonth > date
        ? nthInSourceMonth
        : nthOrLastOfMonth(date.plusMonths(1), dayOfWeek, param);
    }
  }
  return undefined;
}

export function getRepeatDates(opts: {
  repeatBasis: EnumTaskRepeatBasis;
  repeatParameter?: number;
  date: Maybe<LocalDate>;
  count: number;
}) {
  const results: LocalDate[] = [];
  let date = opts.date;
  for (let i = 0; i < opts.count; i++) {
    date = getRepeatDate({
      date,
      repeatBasis: opts.repeatBasis,
      repeatParameter: opts.repeatParameter,
    });
    if (date) {
      results.push(date);
    } else {
      return results;
    }
  }
  return results;
}

export interface VisitWithRepeatIndex {
  visit: PlannedSalesVisit;
  repeatIndex: number;
}

/**
 * Repeat the visit until either limit is reached.
 * maxRepeatCount is the total number of instances, including the original
 * visit, so a count of 10 returns 10 visits. A hard limit of 100 is in placed
 * to prevent repeating indefinitely.
 * maxRepeatDate is inclusive. Repeats follow the visit repeat basis up to and
 * including that date. A daily visit across 90 days, from the start date
 * through 89 days later, returns 90 visits.
 * When both limits are set, the series stops at the first one reached.
 * When neither is set, only the original visit is returned.
 */
export function getVisitWithRepeats(
  visit: PlannedSalesVisit,
  opts: {
    maxRepeatCount?: Maybe<number>;
    maxRepeatDate?: Maybe<LocalDate>;
  }
): VisitWithRepeatIndex[] {
  const results: VisitWithRepeatIndex[] = [{ visit, repeatIndex: 0 }];
  if (isNullish(opts.maxRepeatCount) && isNullish(opts.maxRepeatDate)) {
    return results;
  }

  const maxRepeatCount = Math.min(100, opts.maxRepeatCount ?? 100);

  let date = visit.date;
  while (results.length < maxRepeatCount) {
    const nextDate = getRepeatDate({
      date,
      repeatBasis: visit.repeatBasis,
      repeatParameter: visit.repeatParameter,
    });
    if (!nextDate || !nextDate.isAfter(date)) {
      break;
    }
    if (
      isNotNullOrUndefined(opts.maxRepeatDate) &&
      nextDate.isAfter(opts.maxRepeatDate)
    ) {
      break;
    }

    const clone = visit.clone();
    clone.date = nextDate;
    results.push({ visit: clone, repeatIndex: results.length });
    date = nextDate;
  }

  return results;
}
